<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Numbered "how it works" flow. Horizontal reads as a row of cards; vertical
 * reads as a connected timeline down the left edge.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const steps = computed(() => props.data.steps ?? [])
const isHorizontal = computed(() => (props.data.orientation ?? 'horizontal') === 'horizontal')

const columnsClass = computed(() => {
  const n = steps.value.length
  if (n <= 1) return 'mx-auto max-w-md'
  if (n === 2) return 'sm:grid-cols-2'
  if (n === 4) return 'sm:grid-cols-2 lg:grid-cols-4'
  return 'sm:grid-cols-2 lg:grid-cols-3'
})
</script>

<template>
  <div>
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.headline"
      :subtitle="data.subtext"
      class="mb-8"
    />

    <!-- Horizontal: cards -->
    <div
      v-if="steps.length && isHorizontal"
      class="grid gap-5"
      :class="columnsClass"
    >
      <div
        v-for="(step, i) in steps"
        :key="i"
      >
        <span
          class="site-bg-accent-soft site-text-accent site-display flex size-10 items-center justify-center rounded-full text-base"
        >{{ i + 1 }}</span>
        <img
          v-if="step.image"
          :src="step.image"
          :alt="step.title"
          class="mt-4 w-full rounded-[var(--site-radius)] object-cover"
          loading="lazy"
        >
        <h4 class="site-display mt-4 text-lg tracking-tight">
          {{ step.title }}
        </h4>
        <p
          v-if="step.description"
          class="mt-2 text-sm leading-relaxed text-[var(--site-muted)]"
        >
          {{ step.description }}
        </p>
      </div>
    </div>

    <!-- Vertical: timeline with a connecting rule -->
    <ol
      v-else-if="steps.length"
      class="mx-auto max-w-2xl"
    >
      <li
        v-for="(step, i) in steps"
        :key="i"
        class="relative flex gap-4 pb-8 last:pb-0"
      >
        <span
          v-if="i < steps.length - 1"
          class="absolute left-5 top-11 h-[calc(100%-2.75rem)] w-px bg-[var(--site-border)]"
          aria-hidden="true"
        />
        <span
          class="site-bg-accent-soft site-text-accent site-display relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full text-base"
        >{{ i + 1 }}</span>
        <div class="min-w-0 pt-1.5">
          <h4 class="site-display text-lg tracking-tight">
            {{ step.title }}
          </h4>
          <p
            v-if="step.description"
            class="mt-2 text-sm leading-relaxed text-[var(--site-muted)]"
          >
            {{ step.description }}
          </p>
          <img
            v-if="step.image"
            :src="step.image"
            :alt="step.title"
            class="mt-3 w-full rounded-[var(--site-radius)] object-cover"
            loading="lazy"
          >
        </div>
      </li>
    </ol>
  </div>
</template>
